import { type CSSProperties } from "react";

interface Props {
  name?: string;
  level?: number;
  hp: number;
  hpMax: number;
  mp?: number;
  mpMax?: number;
  xp?: number;
  xpMax?: number;
}

const wrapStyle: CSSProperties = {
  position: "fixed",
  bottom: 8,
  right: 8,
  width: 240,
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding: "8px 10px",
  zIndex: 990,
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  fontSize: 12,
  color: "#e6e8ee",
  pointerEvents: "none",
};

const barOuter: CSSProperties = {
  height: 8,
  background: "rgba(255,255,255,0.08)",
  borderRadius: 3,
  overflow: "hidden",
  marginTop: 3,
};

const barInner = (frac: number, color: string): CSSProperties => ({
  width: `${Math.round(frac * 100)}%`,
  height: "100%",
  background: color,
  transition: "width 120ms linear",
});

/**
 * HP / MP / XP bars + level + name. Pure-DOM, drop on every scene that has
 * a "player" entity.
 */
export function PlayerStatusBar({
  name = "Hero",
  level,
  hp,
  hpMax,
  mp,
  mpMax,
  xp,
  xpMax,
}: Props) {
  const hpFrac = hpMax > 0 ? hp / hpMax : 0;
  const mpFrac = mp !== undefined && mpMax !== undefined && mpMax > 0 ? mp / mpMax : undefined;
  const xpFrac = xp !== undefined && xpMax !== undefined && xpMax > 0 ? xp / xpMax : undefined;
  return (
    <div style={wrapStyle} data-testid="player-status-bar">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong>{name}</strong>
        {level !== undefined && (
          <span style={{ color: "#fbbf24", fontWeight: 600 }}>Lv {level}</span>
        )}
      </div>
      <div style={{ marginTop: 6, opacity: 0.85 }}>
        HP {Math.round(hp)} / {Math.round(hpMax)}
      </div>
      <div style={barOuter}>
        <div
          style={barInner(
            hpFrac,
            hpFrac > 0.5 ? "#4ade80" : hpFrac > 0.25 ? "#facc15" : "#ef4444",
          )}
        />
      </div>
      {mpFrac !== undefined && (
        <>
          <div style={{ marginTop: 6, opacity: 0.85 }}>
            MP {Math.round(mp ?? 0)} / {Math.round(mpMax ?? 0)}
          </div>
          <div style={barOuter}>
            <div style={barInner(mpFrac, "#60a5fa")} />
          </div>
        </>
      )}
      {xpFrac !== undefined && (
        <>
          <div style={{ marginTop: 6, opacity: 0.7, fontSize: 11 }}>
            XP {Math.round(xp ?? 0)} / {Math.round(xpMax ?? 0)}
          </div>
          <div style={{ ...barOuter, height: 4 }}>
            <div style={barInner(xpFrac, "#a855f7")} />
          </div>
        </>
      )}
    </div>
  );
}
